import moment from 'moment';
import * as React from 'react';
import { useCookies } from 'react-cookie';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { AuthContext } from '../context/auth.context';
import { JurorContext } from '../context/juror.context';
import Account from '../models/account';
import { JUROR_GROUP_PHASES } from '../models/jurorGroup';
import Session from '../models/session';

export const authProvider = {
  isAuthenticated: false,
  async signin(email, password) {
    const token = 'Basic ' + btoa(`${email}:${password}`);

    return fetch('/if-api/backoffice/auth', {
      headers: {
        Authorization: token,
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.statusCode === 200) {
          this.isAuthenticated = true;
        }
        return res;
      })
      .catch((error) => {
        console.error('Error:', error);
        return new Error('Login failed');
      });
  },
  async signout() {
    clearSessionCookie('JSESSIONID');
    return true;
  },
};

export const useAuth = () => {
  return React.useContext(AuthContext);
};

export const RequireAuth = ({ children }) => {
  let auth = useAuth();
  let location = useLocation();

  if (!auth.isAuthenticated) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = React.useState(null);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [sessionCookie, setSessionCookie] = React.useState('');

  const signin = async (email, password) => {
    const data = await authProvider.signin(email, password);
    if (data instanceof Error) return data;

    setIsAuthenticated(true);
    setUser(data);

    // Removing a previously persisted juror_uuid to prevent session glitches, when doing a refresh in Preview mode.
    localStorage.removeItem('juror_uuid');

    return data;
  };

  const signout = (callback) => {
    setUser(null);
    setIsAuthenticated(false);
    callback();
  };

  let value = {
    user,
    isAuthenticated,
    sessionCookie,
    setSessionCookie,
    signin,
    signout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useJuror = () => {
  return React.useContext(JurorContext);
};

export const RequireJuror = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { signIn, isLoading, isAuthenticated } = useJuror();

  React.useEffect(async () => {
    if (!isAuthenticated && !isLoading) {
      const uuid = id || localStorage.getItem('juror_uuid');
      let data = null;

      if (uuid) {
        data = await signIn(uuid);
      }

      if (!data && location.pathname !== '/juror') navigate('/juror');
    }
  }, [isAuthenticated, isLoading, location, id]);

  return children;
};

export const JurorProvider = ({ children }) => {
  const [juror, setJuror] = React.useState(null);
  const [jurorGroup, setJurorGroup] = React.useState(null);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [session, setSession] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [cookies] = useCookies(['isJurorPreview']);
  const navigate = useNavigate();

  const signIn = async (uuid) => {
    setIsLoading(true);
    const accountData = await Account.fetch(uuid);

    if (!accountData) {
      setIsLoading(false);
      return false;
    }

    if (!cookies?.isJurorPreview) {
      localStorage.setItem('juror_uuid', uuid);
    }

    const sessionData = await Session.fetch(accountData.sessionId);

    setIsAuthenticated(true);
    setJuror(accountData);
    setJurorGroup(
      accountData?.jurorGroups.length > 0 ? accountData?.jurorGroups[0] : []
    );
    setSession(sessionData);
    setIsLoading(false);

    if (
      accountData?.jurorGroups?.[0].jurorGroupPhase ==
      JUROR_GROUP_PHASES.completed
    ) {
      const now = moment(new Date());
      const sessionEndDate = moment(sessionData?.endDate).add(1, 'w');

      if (now.isAfter(sessionEndDate)) {
        navigate('/closed');
      }
    }
    return accountData;
  };

  const updateRole = (role) => {
    setJurorGroup((prev) => ({ ...prev, role }));
  };

  const selectJurorGroup = (jurorGroupId) => {
    const jurorGroup = juror.jurorGroups.find(
      (group) => group.jurorGroupId == jurorGroupId
    );
    setJurorGroup(jurorGroup);
  };

  const clearJurorAuth = () => {
    setJuror(null);
    setJurorGroup(null);
    setIsAuthenticated(false);
    setSession(null);
  };

  let value = {
    juror,
    isLoading,
    session,
    signIn,
    isAuthenticated,
    role: jurorGroup?.role,
    updateRole,
    jurorGroup,
    setJurorGroup,
    selectJurorGroup,
    clearJurorAuth,
    setSession,
    isPreview: cookies?.isJurorPreview,
  };

  return (
    <JurorContext.Provider value={value}>{children}</JurorContext.Provider>
  );
};
